import { AIChatbotEvents, trackAIChatbotEvent } from 'Analytics/ai-chatbot-events';
import Shepherd from 'shepherd.js';

import { lockScroll, unlockScroll } from 'Utils/dom';
import { isDesktop, isLandscape } from 'Utils/viewport';

const VaiaProductTour = (compact = false) => {
    const _TOOLTIP_BACKDROP_TRANSITION_DURATION = 300;

    const addProgress = () => {
        const currentStep = tour?.getCurrentStep();
        const currentStepElement = currentStep?.getElement();
        const footer = currentStepElement?.querySelector('.shepherd-footer');
        const progress = document.createElement('span');

        progress.innerText = `${tour?.steps.indexOf(currentStep) + 1} of ${tour?.steps.length}`;
        progress.classList.add('shepherd-progress');

        footer?.insertAdjacentElement('afterbegin', progress);
    };

    // Resolved when the step shows - the suggestion lives in the trigger's shadow root
    const mobileTriggerAnchor = () => document.querySelector('vm-chat-mobile-trigger')?.firstSuggestion;

    const compactAttachTo = (() => {
        if (!isDesktop()) {
            return {
                element: mobileTriggerAnchor,
                on: 'bottom-start',
            };
        }

        return {
            element: '.js_vmChatbotInline .js_chatbotPlaceholderSuggestion:nth-child(1)',
            on: 'left',
        };
    })();

    const compactSteps = [
        {
            id: 'vaia-product-tour-step-compact-1',
            title: 'Click here to read the summary <i class="icon icon-chatbot"></i>',
            text: "Generate an AI summary and review what's discussed in this presentation.",
            attachTo: compactAttachTo,
            buttons: [],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_APPEAR);
                },
            },
        },
    ];

    const desktopSteps = [
        {
            id: 'vaia-product-tour-step-1',
            title: 'Save time with Vumedi AI <i class="icon icon-chatbot"></i>',
            text: 'Ask for the main points in a presentation. Responses come from the video’s transcript.',
            attachTo: {
                element: '.js_vmChatbotInline .js_chatbotPlaceholderSuggestion:nth-child(3)',
                on: 'left-start',
            },
            buttons: [
                {
                    action() {
                        trackAIChatbotEvent(AIChatbotEvents.INFO_MODAL_CLICKED);
                        return this.next();
                    },
                    classes: 'vm-button vm-button--sm vm-button--secondary',
                    text: 'Next',
                },
            ],
            extraHighlights: ['.js_vmChatbotInline'],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.INFO_MODAL_APPEAR);
                },
            },
        },
        {
            id: 'vaia-product-tour-step-2',
            title: 'Click the button to try it now <i class="icon icon-chatbot"></i>',
            text: 'Quickly generate an AI summary while you watch the video.',
            attachTo: {
                element: '.js_vmChatbotInline .js_chatbotPlaceholderSuggestion:nth-child(1)',
                on: 'left-start',
            },
            buttons: [
                {
                    action() {
                        return this.next();
                    },
                    classes: 'vm-button vm-button--sm vm-button--secondary vm-button--try-it-now',
                    text: 'Try it now',
                },
            ],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_APPEAR);
                },
            },
        },
    ];

    const mobileSteps = [
        {
            id: 'vaia-product-tour-step-mobile-1',
            title: `<span>New Update</span><span>Save time with Vumedi AI</span>`,
            text: 'Ask for the main points in a presentation. Responses come from the video’s transcript.',
            buttons: [
                {
                    action() {
                        trackAIChatbotEvent(AIChatbotEvents.INFO_MODAL_CLICKED);
                        return this.next();
                    },
                    classes: 'vm-button vm-button--sm vm-button--primary',
                    text: 'Continue',
                },
            ],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.INFO_MODAL_APPEAR);
                    window.scrollTo({ top: 0 });
                    setTimeout(() => {
                        // The tour may have ended during the backdrop transition
                        if (!tour.isActive()) {
                            return;
                        }
                        // Paired here so only the tour that locks unlocks, exactly once, however it ends
                        lockScroll();
                        tour.once('inactive', unlockScroll);
                    }, _TOOLTIP_BACKDROP_TRANSITION_DURATION);
                },
            },
        },
        {
            id: 'vaia-product-tour-step-mobile-2',
            title: 'Tap the button to try it now',
            text: 'Quickly generate an AI summary while you watch the video.',
            attachTo: {
                element: mobileTriggerAnchor,
                on: 'bottom-start',
            },
            buttons: [],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_APPEAR);
                    window.scrollTo({ top: 0 });
                },
            },
        },
    ];

    const tourClass = (() => {
        if (compact) {
            return 'vm-product-tour--compact';
        }

        if (!isDesktop()) {
            return 'vm-product-tour--mobile';
        }

        return 'vm-product-tour--desktop';
    })();

    const tour = new Shepherd.Tour({
        defaultStepOptions: {
            cancelIcon: {
                enabled: false,
            },
            classes: ['vm-product-tour', 'vm-product-tour--vaia', tourClass].join(' '),
            scrollTo: !isDesktop() ? { behavior: 'smooth', block: 'nearest' } : false,
            modalOverlayOpeningRadius: !isDesktop() ? 8 : 12,
            when: {
                show() {
                    if (tourClass === 'vm-product-tour--desktop') {
                        addProgress();
                    }
                },
            },
        },
        useModalOverlay: true,
    });

    const _QUESTION_CLASSES = [
        'js_chatbotPlaceholderSuggestion',
        'vm-button--chatbot-send',
        'vm-chat-mobile-trigger__suggestion',
    ];

    // Listens in the capture phase so the tour releases its scroll lock before the mobile
    // trigger's click handler scrolls the video into place and opens the sheet. `composedPath`
    // reaches into the trigger's shadow root, where the mobile suggestions live.
    const closeProductTour = (ev) => {
        const isQuestionClick = ev
            .composedPath()
            .some((node) => _QUESTION_CLASSES.some((className) => node.classList?.contains(className)));

        if (isQuestionClick) {
            tour.cancel();
        }
    };

    // Kept from hiding, so restoring works even after the chat moved into the mobile sheet
    let hiddenCollapseButton = null;

    const toggleCollapseButton = (display = false) => {
        if (display) {
            hiddenCollapseButton?.style.removeProperty('display');
            hiddenCollapseButton = null;
            return;
        }

        hiddenCollapseButton = document.querySelector('.js_vmChatbotInline .js_chatbotCollapse');
        hiddenCollapseButton?.style.setProperty('display', 'none');
    };

    const cancelDueToLandscape = () => {
        tour.landscapeCancelled = true;
        tour.cancel();
    };

    const handleResize = () => {
        if (isLandscape()) {
            cancelDueToLandscape();
        }
    };

    const steps = (() => {
        if (compact) {
            return compactSteps;
        }

        if (!isDesktop()) {
            return mobileSteps;
        }

        return desktopSteps;
    })();

    steps.forEach((step) => tour.addStep(step));

    tour.on('active', () => {
        document.addEventListener('click', closeProductTour, { capture: true });
        toggleCollapseButton();

        if (!isDesktop()) {
            if (isLandscape()) {
                cancelDueToLandscape();
                return;
            }
            window.addEventListener('resize', handleResize);
        }
    });

    const cleanupTour = () => {
        document.removeEventListener('click', closeProductTour, { capture: true });
        toggleCollapseButton(true);

        if (!isDesktop()) {
            window.removeEventListener('resize', handleResize);
        }
    };

    tour.on('cancel', cleanupTour);
    tour.on('complete', cleanupTour);

    return tour;
};

export { VaiaProductTour };
